import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { cn } from 'ethcss'
import translate from '../../../core/translate'
import Modal from '../../../blocks.simple/modal/modal'
import Preview from '../../../blocks.app/preview/preview'
import Icon from '../../../blocks.simple/icon/icon'
import { Checkbox } from '../../../atoms/Checkbox'
import { Radio } from '../../../atoms/Radio'
import { Button } from '../../../molecules/Button'
import { ModalSide } from '../../../molecules/ModalSide/ModalSide'
import { color } from '../../../theme/tokens/color'
import {
    ContentModerationDecision,
    ContentModerationStatus,
    IContentModerationItem,
    IContentModerationProps,
} from './contentModeration.types'
import styles from './contentModeration.jcss'
import { api } from '../../../core/api/ConnectionManager'

const statusTranslation: Record<ContentModerationStatus, string> = {
    pending: 'contentPendingModeration',
    approved: 'contentApproved',
    rejected: 'contentRejected',
}

const getStatusClass = (status: ContentModerationStatus): string => {
    if (status === 'approved') return styles.statusApproved
    if (status === 'rejected') return styles.statusRejected

    return styles.statusPending
}

const getPreviewFileType = (filename: string): 'image' | 'video' =>
    /\.(mp4|webm|mov|m4v|ogv)$/i.test(filename) ? 'video' : 'image'

const getThumbnailUrl = (item: IContentModerationItem): string => item.thumbnailUrl || item.fileUrl

const renderThumbnail = (item: IContentModerationItem, className: string): React.ReactElement => {
    if (getPreviewFileType(item.filename) === 'video') {
        return (
            <div className={cn(className, styles.videoThumbnail)}>
                <Icon type="play" size={20} color={color.iconWhiteUltraHigh} />
            </div>
        )
    }

    return <img className={className} src={getThumbnailUrl(item)} alt="" />
}

export const ContentModeration: React.FC<IContentModerationProps> = ({ onPendingCountChange }) => {
    const [items, setItems] = useState<IContentModerationItem[]>([])
    const [selectedIds, setSelectedIds] = useState<number[]>([])
    const [reviewItem, setReviewItem] = useState<IContentModerationItem | null>(null)
    const [detailsItem, setDetailsItem] = useState<IContentModerationItem | null>(null)
    const [previewItem, setPreviewItem] = useState<IContentModerationItem | null>(null)
    const [deleteIds, setDeleteIds] = useState<number[]>([])
    const [decision, setDecision] = useState<ContentModerationDecision | null>(null)
    const [comment, setComment] = useState('')
    const [creativesTotalCount, setCreativesTotalCount] = useState<number>(0)
    const [isCreativesLoading, setIsCreativesLoading] = useState(false)
    const nextPageTokenRef = useRef<string | null | undefined>(undefined)
    const isCreativesLoadingRef = useRef(false)
    const rowsScrollAreaRef = useRef<HTMLDivElement>(null)
    const pendingCount = useMemo(() => items.filter((item) => item.status === 'pending').length, [items])
    const allSelected = items.length > 0 && selectedIds.length === items.length
    const previewItems = previewItem
        ? [
              {
                  id: previewItem.id,
                  src: previewItem.fileUrl,
                  fileType: getPreviewFileType(previewItem.filename),
                  name: previewItem.filename,
                  folderId: 0,
                  data: '',
                  isDirectory: 0,
                  type: 'file',
                  displayAs: 'file',
                  thumbnail: getThumbnailUrl(previewItem),
                  style: {},
                  textOrientation: 'horizontal' as const,
              },
          ]
        : []

    const getCreatives = useCallback(async (pageToken?: string): Promise<void> => {
        if (isCreativesLoadingRef.current || nextPageTokenRef.current === null) return

        isCreativesLoadingRef.current = true
        setIsCreativesLoading(true)

        try {
            const res: any = await api.send(
                'doohGetCreatives',
                pageToken
                    ? { nextPageToken: pageToken }
                    : {
                          limit: 15,
                          // status: 'pending',
                          // supplierId: 8,
                      }
            )

            if (Array.isArray(res.creatives)) {
                const loadedItems: IContentModerationItem[] = res.creatives.map((item: any) => ({
                    id: item.id,
                    filename: item.filename,
                    fileUrl: item.fileUrl,
                    status: item.status,
                    reason: item.reason,
                    creativeId: item.creativeId,
                }))

                setItems((currentItems) => {
                    const loadedIds = new Set(currentItems.map((item) => item.id))
                    const uniqueItems = loadedItems.filter((item) => !loadedIds.has(item.id))

                    return [...currentItems, ...uniqueItems]
                })
            }

            if (typeof res.totalCount === 'number') {
                setCreativesTotalCount(res.totalCount)
            }

            nextPageTokenRef.current = typeof res.nextPageToken === 'string' ? res.nextPageToken : null
        } catch {
            return
        } finally {
            isCreativesLoadingRef.current = false
            setIsCreativesLoading(false)
        }
    }, [])

    const handleRowsScroll = (event: React.UIEvent<HTMLDivElement>): void => {
        const nextPageToken = nextPageTokenRef.current

        if (nextPageToken === null || isCreativesLoadingRef.current) return

        const scrollContainer = event.currentTarget
        const distanceToBottom = scrollContainer.scrollHeight - scrollContainer.scrollTop - scrollContainer.clientHeight

        if (distanceToBottom <= 160) {
            getCreatives(nextPageToken)
        }
    }

    useEffect(() => {
        getCreatives()
    }, [getCreatives])

    useEffect(() => {
        const scrollContainer = rowsScrollAreaRef.current
        const nextPageToken = nextPageTokenRef.current

        if (!scrollContainer || isCreativesLoading || nextPageToken === null) return

        const distanceToBottom = scrollContainer.scrollHeight - scrollContainer.scrollTop - scrollContainer.clientHeight

        if (distanceToBottom <= 160) {
            getCreatives(nextPageToken)
        }
    }, [getCreatives, isCreativesLoading, items.length])

    useEffect(() => {
        const listenersId: string[] = []

        api.addObserver(
            'doohCreativeCreated',
            (data: any) => {
                if (!data) return

                setItems((currentItems) => [data, ...currentItems])
                setCreativesTotalCount((currentCount) => currentCount + 1)
            },
            listenersId
        )
        api.addObserver(
            'doohCreativeUpdated',
            (data: any) => {
                if (!data) return

                setItems((currentItems) =>
                    currentItems.map((item) => {
                        if (item.id === data.id) {
                            return {
                                id: data.id,
                                filename: data.filename,
                                fileUrl: data.fileUrl,
                                status: data.status,
                                reason: data.reason,
                                creativeId: data.creativeId,
                            }
                        }

                        return item
                    })
                )
            },
            listenersId
        )
        api.addObserver(
            'doohCreativeDeleted',
            (data: any) => {
                if (!data) return

                setItems((currentItems) => currentItems.filter((item) => !data.ids.includes(item.id)))
                setCreativesTotalCount((currentCount) => Math.max(0, currentCount - data.ids.length))
            },
            listenersId
        )

        return () => {
            listenersId.forEach((id) => api.removeObserver(id))
        }
    }, [])

    // useEffect(() => {
    //     onPendingCountChange(pendingCount)
    // }, [onPendingCountChange, pendingCount])

    const toggleItem = (itemId: number): void => {
        setSelectedIds((ids) => (ids.includes(itemId) ? ids.filter((id) => id !== itemId) : [...ids, itemId]))
    }

    const toggleAll = (): void => {
        setSelectedIds(allSelected ? [] : items.map((item) => item.id))
    }

    const openReview = (item: IContentModerationItem): void => {
        setReviewItem(item)
        setDecision(null)
        setComment('')
    }

    const closeReview = (): void => {
        setReviewItem(null)
        setDecision(null)
        setComment('')
    }

    const saveReview = (): void => {
        if (!reviewItem || !decision) return

        const moderationPayload = {
            id: reviewItem.id,
            status: decision,
            ...(decision === 'rejected' ? { reason: comment } : {}),
        }

        api.send('doohModerateCreative', moderationPayload)

        closeReview()
    }

    const confirmDelete = (): void => {
        if (!deleteIds.length) return

        api.send('doohDeleteCreatives', {
            ids: deleteIds,
        }).then(() => {
            setDeleteIds([])
        })
    }

    const renderFile = (item: IContentModerationItem): React.ReactElement => (
        <div className={styles.modalFile}>
            {renderThumbnail(item, styles.modalThumbnail)}
            <div className={styles.contentMeta}>
                <div className={styles.contentName}>{item.filename}</div>
                <button type="button" className={styles.preview} onClick={() => setPreviewItem(item)}>
                    {translate('preview')}
                </button>
            </div>
        </div>
    )

    const renderTable = (): React.ReactElement => (
        <div className={styles.scrollArea}>
            <div className={styles.table}>
                <div className={cn(styles.row, styles.header)}>
                    <div className={cn(styles.cell, styles.checkboxCell)}>
                        <Checkbox checked={allSelected} onClick={toggleAll} />
                    </div>
                    <div className={styles.cell}>{translate('contentForModeration')}</div>
                    <div className={styles.cell}>{translate('status')}</div>
                    <div className={styles.cell} />
                    <div className={cn(styles.cell, styles.checkboxCell)}>
                        <button
                            type="button"
                            className={styles.deleteButton}
                            disabled={!selectedIds.length}
                            onClick={() => setDeleteIds(selectedIds)}
                            aria-label={translate('deleteContent')}
                            title={translate('deleteContent')}
                        >
                            <Icon type="delete" size={16} color={color.iconDarkMedium} />
                        </button>
                    </div>
                </div>
                <div ref={rowsScrollAreaRef} className={styles.rowsScrollArea} onScroll={handleRowsScroll}>
                    {items.map((item) => (
                        <div className={styles.row} key={item.id}>
                            <div className={cn(styles.cell, styles.checkboxCell)}>
                                <Checkbox checked={selectedIds.includes(item.id)} onClick={() => toggleItem(item.id)} />
                            </div>
                            <div className={cn(styles.cell, styles.contentCell)}>
                                {renderThumbnail(item, styles.thumbnail)}
                                <div className={styles.contentMeta}>
                                    <div className={styles.contentName}>{item.filename}</div>
                                    <button
                                        type="button"
                                        className={styles.preview}
                                        onClick={() => setPreviewItem(item)}
                                    >
                                        {translate('preview')}
                                    </button>
                                </div>
                            </div>
                            <div className={cn(styles.cell, styles.status, getStatusClass(item.status))}>
                                {translate(statusTranslation[item.status])}
                            </div>
                            <div className={styles.cell}>
                                <Button
                                    className={styles.actionButton}
                                    onClick={() =>
                                        item.status === 'pending' ? openReview(item) : setDetailsItem(item)
                                    }
                                >
                                    {translate(item.status === 'pending' ? 'moderation' : 'details')}
                                </Button>
                            </div>
                            <div className={cn(styles.cell, styles.checkboxCell)}>
                                <button
                                    type="button"
                                    className={cn(styles.deleteButton, styles.rowDeleteButton)}
                                    data-content-delete-button="true"
                                    onClick={() => setDeleteIds([item.id])}
                                    aria-label={translate('delete')}
                                >
                                    <Icon type="delete" size={16} color={color.iconDarkMedium} />
                                </button>
                            </div>
                        </div>
                    ))}
                </div>
                <div className={styles.pagination}>
                    <span>{items.length}</span>
                    <span>{translate('from')}</span>
                    <span className={styles.paginationLink}>{creativesTotalCount}</span>
                </div>
            </div>
        </div>
    )

    return (
        <div className={styles.wrapper}>
            {items.length ? (
                renderTable()
            ) : (
                <div className={styles.empty}>
                    <img className={styles.emptyIcon} src="/images/externalAdvertisement/moderation-empty.svg" alt="" />
                    <div className={styles.emptyTitle}>{translate('moderationEmptyTitle')}</div>
                    <div className={styles.emptyText}>{translate('moderationEmptyDescription')}</div>
                </div>
            )}

            <Modal open={Boolean(reviewItem)} mod="black" showCloseIcon={false}>
                {reviewItem && (
                    <ModalSide
                        position="right"
                        title={translate('contentModerationTitle')}
                        onClose={closeReview}
                        onReject={closeReview}
                        onAccept={saveReview}
                        acceptDisabled={!decision || (decision === 'rejected' && !comment.trim())}
                    >
                        <div className={styles.sideContent}>
                            {renderFile(reviewItem)}
                            <div className={styles.question}>{translate('approveContentQuestion')}</div>
                            <div className={styles.description}>{translate('moderationDescription')}</div>
                            <Radio
                                selected={decision === 'approved'}
                                onClick={() => setDecision('approved')}
                                label={translate('accept')}
                                className={styles.radio}
                            />
                            <Radio
                                selected={decision === 'rejected'}
                                onClick={() => setDecision('rejected')}
                                label={translate('refuse')}
                            />
                            {decision === 'rejected' && (
                                <>
                                    <label className={styles.commentLabel} htmlFor="content-moderation-comment">
                                        {translate('rejectReason')}
                                    </label>
                                    <textarea
                                        id="content-moderation-comment"
                                        className={styles.textarea}
                                        value={comment}
                                        maxLength={10000}
                                        placeholder={translate('moderatorCommentPlaceholder')}
                                        onChange={(event) => setComment(event.target.value)}
                                    />
                                    <div className={styles.counter}>{comment.length} / 10000</div>
                                </>
                            )}
                            <div className={styles.notice}>
                                <Icon type="info" size={18} color={color.info} />
                                <div>
                                    <div className={styles.noticeTitle}>{translate('attention')}</div>
                                    <div className={styles.noticeText}>{translate('moderationDecisionWarning')}</div>
                                </div>
                            </div>
                        </div>
                    </ModalSide>
                )}
            </Modal>

            <Modal open={Boolean(detailsItem)} mod="black" showCloseIcon={false}>
                {detailsItem && (
                    <div className={styles.dialogOverlay}>
                        <div className={styles.dialog}>
                            <button
                                type="button"
                                className={styles.dialogClose}
                                onClick={() => setDetailsItem(null)}
                                aria-label={translate('close')}
                            >
                                <Icon type="close_tiny" size={18} color={color.iconDarkUltraHigh} />
                            </button>
                            <div className={styles.dialogBody}>
                                <div className={styles.dialogTitle}>{translate('moderationDetails')}</div>
                                {renderFile(detailsItem)}
                                <div className={cn(styles.detailStatus, getStatusClass(detailsItem.status))}>
                                    {translate(statusTranslation[detailsItem.status])}
                                </div>
                                {detailsItem.status === 'rejected' && (
                                    <>
                                        <div className={styles.commentLabel}>{translate('rejectReason')}</div>
                                        <div className={styles.detailComment}>
                                            {detailsItem.reason || translate('noComment')}
                                        </div>
                                    </>
                                )}
                            </div>
                            <div className={styles.dialogFooter}>
                                <Button onClick={() => setDetailsItem(null)}>{translate('close')}</Button>
                            </div>
                        </div>
                    </div>
                )}
            </Modal>

            <Modal open={deleteIds.length > 0} mod="black" showCloseIcon={false}>
                <div className={styles.dialogOverlay}>
                    <div className={styles.dialog}>
                        <button
                            type="button"
                            className={styles.dialogClose}
                            onClick={() => setDeleteIds([])}
                            aria-label={translate('close')}
                        >
                            <Icon type="close_tiny" size={18} color={color.iconDarkUltraHigh} />
                        </button>
                        <div className={styles.dialogBody}>
                            <div className={styles.dialogTitle}>{translate('deleteContentQuestion')}</div>
                            <div className={styles.description}>{translate('deleteContentWarning')}</div>
                        </div>
                        <div className={styles.dialogFooter}>
                            <Button onClick={() => setDeleteIds([])}>{translate('goBack')}</Button>
                            <Button className={styles.dangerButton} onClick={confirmDelete}>
                                {translate('delete')}
                            </Button>
                        </div>
                    </div>
                </div>
            </Modal>

            <Preview
                isOpen={Boolean(previewItem)}
                isShowArrows={false}
                currentIndex={0}
                items={previewItems}
                onClose={() => setPreviewItem(null)}
            />
        </div>
    )
}
